/**
 * Cash Flow Table - Business Value Calculator
 * Sprint 7: Day 3
 *
 * Displays 36-month cash flow projection with:
 * - Monthly costs and benefits
 * - Net cash flow per month
 * - Cumulative cash flow (running total)
 * - Break-even detection (when cumulative turns positive)
 * - Scrollable table with sticky header
 */

import React from 'react';

export interface CashFlowRow {
  month: number;
  costs: number;
  benefits: number;
  netCashFlow: number;
  cumulativeCashFlow: number;
}

interface CashFlowTableProps {
  cashFlowData: CashFlowRow[];
  breakEvenMonth: number | null;  // Month when cumulative turns positive
}

export const CashFlowTable: React.FC<CashFlowTableProps> = ({
  cashFlowData,
  breakEvenMonth
}) => {
  // Format currency
  const formatCurrency = (value: number) => {
    const prefix = value >= 0 ? '$' : '-$';
    return `${prefix}${Math.abs(value).toLocaleString()}`;
  };

  // Get color for cash flow value
  const getColor = (value: number) => {
    if (value > 0) return '#48bb78';  // Green
    if (value === 0) return '#718096'; // Gray
    return '#fc8181';  // Red
  };

  return (
    <div className="cash-flow-table-container">
      <div className="cash-flow-header">
        <h3 className="cash-flow-title">36-Month Cash Flow Projection</h3>
        <p className="cash-flow-subtitle">
          Monthly costs, benefits, and cumulative cash flow over 3 years
          {breakEvenMonth && (
            <span className="break-even-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Break-even: Month {breakEvenMonth}
            </span>
          )}
        </p>
      </div>

      <div className="cash-flow-table-scroll">
        <table className="cash-flow-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', position: 'sticky', left: 0, background: '#f7fafc', zIndex: 2 }}>Month</th>
              <th style={{ textAlign: 'right' }}>Costs</th>
              <th style={{ textAlign: 'right' }}>Benefits</th>
              <th style={{ textAlign: 'right' }}>Net Cash Flow</th>
              <th style={{ textAlign: 'right' }}>Cumulative</th>
            </tr>
          </thead>
          <tbody>
            {cashFlowData.map((row) => {
              const isBreakEven = breakEvenMonth === row.month;
              const isCumulativePositive = row.cumulativeCashFlow > 0;

              return (
                <tr
                  key={row.month}
                  className={`cash-flow-row ${isBreakEven ? 'cash-flow-row-breakeven' : ''}`}
                >
                  <td style={{ textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: isBreakEven ? '#f0fff4' : '#ffffff', zIndex: 1 }}>
                    {isBreakEven && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', marginRight: '0.5rem', color: '#48bb78' }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    )}
                    Month {row.month}
                  </td>
                  <td style={{ textAlign: 'right', color: '#fc8181' }}>
                    {formatCurrency(row.costs)}
                  </td>
                  <td style={{ textAlign: 'right', color: '#48bb78' }}>
                    {formatCurrency(row.benefits)}
                  </td>
                  <td style={{ textAlign: 'right', color: getColor(row.netCashFlow), fontWeight: 600 }}>
                    {formatCurrency(row.netCashFlow)}
                  </td>
                  <td style={{ textAlign: 'right', color: getColor(row.cumulativeCashFlow), fontWeight: 700, fontSize: '0.9375rem' }}>
                    {formatCurrency(row.cumulativeCashFlow)}
                    {isCumulativePositive && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', marginLeft: '0.5rem', color: '#48bb78' }}>
                        <polyline points="18 15 12 9 6 15" />
                      </svg>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 700 }}>36-Month Total</td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: '#fc8181' }}>
                {formatCurrency(cashFlowData.reduce((sum, row) => sum + row.costs, 0))}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: '#48bb78' }}>
                {formatCurrency(cashFlowData.reduce((sum, row) => sum + row.benefits, 0))}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: getColor(cashFlowData.reduce((sum, row) => sum + row.netCashFlow, 0)) }}>
                {formatCurrency(cashFlowData.reduce((sum, row) => sum + row.netCashFlow, 0))}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1rem', color: getColor(cashFlowData[cashFlowData.length - 1].cumulativeCashFlow) }}>
                {formatCurrency(cashFlowData[cashFlowData.length - 1].cumulativeCashFlow)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <style>{`
        .cash-flow-table-container {
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .cash-flow-header {
          margin-bottom: 1.5rem;
        }

        .cash-flow-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 0.5rem;
        }

        .cash-flow-subtitle {
          font-size: 0.875rem;
          color: #718096;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .break-even-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 700;
          color: #48bb78;
          background: rgba(72, 187, 120, 0.1);
          border: 1px solid #48bb78;
          border-radius: 6px;
        }

        .cash-flow-table-scroll {
          max-height: 500px;
          overflow-y: auto;
          overflow-x: auto;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }

        .cash-flow-table {
          width: 100%;
          border-collapse: collapse;
        }

        .cash-flow-table thead th {
          position: sticky;
          top: 0;
          padding: 0.75rem 1rem;
          font-size: 0.75rem;
          font-weight: 700;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: #f7fafc;
          border-bottom: 2px solid #e2e8f0;
          z-index: 10;
        }

        .cash-flow-row td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #f7fafc;
          font-size: 0.875rem;
        }

        .cash-flow-row:hover {
          background: #f7fafc;
        }

        .cash-flow-row-breakeven {
          background: #f0fff4;
          border: 2px solid #48bb78;
        }

        .cash-flow-row-breakeven:hover {
          background: #e6ffed;
        }

        .cash-flow-table tfoot tr {
          border-top: 2px solid #2d3748;
          background: #f7fafc;
        }

        .cash-flow-table tfoot td {
          padding: 1rem;
          font-size: 0.9375rem;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .cash-flow-subtitle {
            flex-direction: column;
            align-items: flex-start;
          }

          .cash-flow-table {
            font-size: 0.8125rem;
          }
        }
      `}</style>
    </div>
  );
};

// Helper function to generate cash flow data
export const generateCashFlowData = (
  totalCosts: number,
  totalBenefits: number,
  costPhasing: number[] = [],  // 36 values summing to 1.0
  benefitPhasing: number[] = [] // 36 values summing to 1.0
): CashFlowRow[] => {
  // Default phasing if not provided
  const defaultCostPhasing = costPhasing.length === 36 ? costPhasing : generateFrontLoadedPhasing(36);
  const defaultBenefitPhasing = benefitPhasing.length === 36 ? benefitPhasing : generateSCurvePhasing(36);

  const cashFlowData: CashFlowRow[] = [];
  let cumulativeCashFlow = 0;

  for (let month = 1; month <= 36; month++) {
    const costs = totalCosts * defaultCostPhasing[month - 1];
    const benefits = totalBenefits * defaultBenefitPhasing[month - 1];
    const netCashFlow = benefits - costs;
    cumulativeCashFlow += netCashFlow;

    cashFlowData.push({
      month,
      costs,
      benefits,
      netCashFlow,
      cumulativeCashFlow
    });
  }

  return cashFlowData;
};

// Generate front-loaded cost phasing (more costs early)
function generateFrontLoadedPhasing(months: number): number[] {
  const phasing: number[] = [];
  let total = 0;

  for (let i = 0; i < months; i++) {
    // Exponential decay: higher early, lower later
    const value = Math.exp(-i / 12);
    phasing.push(value);
    total += value;
  }

  // Normalize to sum to 1.0
  return phasing.map(v => v / total);
}

// Generate S-curve benefit phasing (slow start, ramp up, plateau)
function generateSCurvePhasing(months: number): number[] {
  const phasing: number[] = [];
  let total = 0;

  for (let i = 0; i < months; i++) {
    // Logistic function for S-curve
    const x = (i - months / 2) / (months / 6);
    const value = 1 / (1 + Math.exp(-x));
    phasing.push(value);
    total += value;
  }

  // Normalize to sum to 1.0
  return phasing.map(v => v / total);
}

export default CashFlowTable;
